import React from 'react';
import { Switch, Badge, Alert } from '@/components/ui';
import { CADENCE_SCHEDULES } from '@/routes/roros/launchpad/types';
import { Info } from 'lucide-react';
import { t } from '@/lib/i18n';

/**
 * B24 — Shared cadence editor used in BOTH the Launchpad Step 5 (onboarding)
 * and the Structure Settings → Cadence tab (post-onboarding editing).
 *
 * Extracted from src/routes/roros/launchpad/StepCadence.tsx. The onboarding
 * step delegates all UI to this component; the settings panel wraps it with
 * load / save plumbing against `/api/v1/cadence/schedules`.
 *
 * Behaviour is identical between the two contexts. Capability flags let the
 * embedder disable edits that don't round-trip in that context:
 *   - `planningCycleEditable` — false in settings (planning cycle is managed
 *     through Launchpad, mirrors OperatingModePanel)
 *
 * The `onChange(CadenceData)` contract is preserved verbatim — embedders get
 * the full updated `{schedules, planning_cycle}` object and diff / persist
 * as they see fit (bulk in onboarding, per-row PUT in settings).
 *
 * AC-PW2-3: Schedule (day) and Time columns removed from the Launchpad
 * onboarding view. Detailed scheduling is configured in Structure Settings →
 * Cadence. Only Cadence name + Active toggle remain.
 */

export interface CadenceScheduleData {
  type: string;
  time: string;
  /**
   * Human-readable day label — kept for API compat with callers that still
   * supply it, but no longer rendered in this editor.
   */
  day: string;
  is_active: boolean;
}

export interface CadenceData {
  schedules: CadenceScheduleData[];
  planning_cycle: 'quarterly' | 'trimester';
}

export interface CadenceScheduleEditorProps {
  value: CadenceData;
  onChange: (data: CadenceData) => void;
  operatingMode: string;
  /** Locale selected in previous step — kept for API compat; no longer used. */
  locale?: string;
  /**
   * Whether the planning cycle toggle accepts edits. Defaults to true
   * (onboarding behaviour). Settings passes false so users can't flip the
   * cycle from here — it's managed through the Launchpad wizard.
   */
  planningCycleEditable?: boolean;
  /**
   * AC-PW2-3: timeEditable kept for API compat with existing embedders.
   * The time column has been removed; this prop is a no-op.
   * @deprecated
   */
  timeEditable?: boolean;
  /**
   * AC-PW2-3: scheduleEditable kept for API compat with existing embedders.
   * The schedule column has been removed; this prop is a no-op.
   * @deprecated
   */
  scheduleEditable?: boolean;
  /**
   * Optional heading override. Onboarding shows the full step heading;
   * settings omits it (the tab title serves that purpose).
   */
  showHeading?: boolean;
}

export function CadenceScheduleEditor({
  value,
  onChange,
  operatingMode,
  // locale, timeEditable, scheduleEditable retained in props interface for compat
  // but not used after AC-PW2-3 removed the Schedule/Time columns.
  planningCycleEditable = true,
  showHeading = true,
}: CadenceScheduleEditorProps) {
  const handleToggle = (type: string, isActive: boolean) => {
    const updated = value.schedules.map((s) => {
      if (s.type === type) return { ...s, is_active: isActive };
      return s;
    });
    onChange({ ...value, schedules: updated });
  };

  const handlePlanningCycleToggle = () => {
    if (!planningCycleEditable) return;
    const newCycle =
      value.planning_cycle === 'quarterly' ? 'trimester' : 'quarterly';
    const updated = value.schedules.map((s) => {
      if (s.type === 'quarterly') return { ...s, is_active: newCycle === 'quarterly' };
      if (s.type === 'trimester') return { ...s, is_active: newCycle === 'trimester' };
      return s;
    });
    onChange({ ...value, schedules: updated, planning_cycle: newCycle });
  };

  return (
    <div className="space-y-6" data-testid="cadence-schedule-editor">
      {showHeading && (
        <div>
          <h2 className="text-xl font-semibold text-foreground">{t('Cadence Schedule Review')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('Review and adjust your leadership cadence schedule. These define the rhythm of operational reviews.')}</p>
        </div>
      )}

      {/* Planning cycle toggle */}
      <div className="rounded-lg bg-muted border border-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground">{t('Planning Cycle')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t('Choose between quarterly (4×3 months) or trimester (3×4 months) planning.')}</p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-medium ${
                value.planning_cycle === 'quarterly'
                  ? 'text-primary-600'
                  : 'text-muted-foreground'
              }`}
            >{t("Quarterly")}</span>
            <Switch
              checked={value.planning_cycle === 'trimester'}
              onChange={handlePlanningCycleToggle}
              disabled={!planningCycleEditable}
              // Auto-focus only in the Launchpad wizard (showHeading=true),
              // not inside the Settings tab, where it would steal focus on
              // every tab entry.
              autoFocus={planningCycleEditable && showHeading}
            />
            <span
              className={`text-sm font-medium ${
                value.planning_cycle === 'trimester'
                  ? 'text-primary-600'
                  : 'text-muted-foreground'
              }`}
            >{t("Trimester")}</span>
          </div>
        </div>
        {!planningCycleEditable && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="planning-cycle-readonly-hint">
            {t('Planning cycle is configured through the Launchpad wizard.')}
          </p>
        )}
      </div>

      {/* Cadence table — AC-PW2-3: Zeitplan (Schedule) and Uhrzeit (Time) columns removed.
          Detailed scheduling is configured in Structure Settings → Cadence. */}
      <div className="border border-border rounded-lg [&_thead_tr:first-child_th:first-child]:rounded-tl-lg [&_thead_tr:first-child_th:last-child]:rounded-tr-lg [&_tbody_tr:last-child_td:first-child]:rounded-bl-lg [&_tbody_tr:last-child_td:last-child]:rounded-br-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("Cadence")}</th>
              <th className="text-center px-4 py-3 font-medium text-muted-foreground">{t("Active")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {CADENCE_SCHEDULES.map((cadence) => {
              const scheduleData = value.schedules.find(
                (s) => s.type === cadence.type
              );
              if (!scheduleData) return null;

              const isRecommended = cadence.recommendedFor.includes(operatingMode);
              const isHiddenByPlanningCycle =
                (cadence.type === 'quarterly' &&
                  value.planning_cycle === 'trimester') ||
                (cadence.type === 'trimester' &&
                  value.planning_cycle === 'quarterly');

              if (isHiddenByPlanningCycle) return null;

              return (
                <tr
                  key={cadence.type}
                  className={`${
                    scheduleData.is_active ? 'bg-card' : 'bg-muted'
                  }`}
                  data-cadence-row={cadence.type}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{t(cadence.label)}</span>
                      {isRecommended && (
                        <Badge variant="success" className="text-[10px] px-1.5">{t("Recommended")}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5" data-no-translate>{cadence.description}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {cadence.alwaysActive ? (
                      <Badge variant="secondary" className="text-xs">{t("Always")}</Badge>
                    ) : (
                      <Switch
                        checked={scheduleData.is_active}
                        onChange={() =>
                          handleToggle(cadence.type, !scheduleData.is_active)
                        }
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Operating mode advisory */}
      <Alert variant="info" icon={<Info className="w-4 h-4" />}>{t('Cadences marked as "Recommended" are suggested for')} <strong className="capitalize">{t(operatingMode)}</strong> {t("mode. Deactivating them is allowed but not recommended.")}</Alert>
    </div>
  );
}
